import { useUnmountEffect } from '@react-hookz/web';
import {
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FieldError } from 'react-hook-form';

import { Button } from '@/components/Button/Button';
import { RegularCalendar, RegularXmark } from '@/icons/components';
import { IconComponent } from '@/icons/iconTypes';
import { twJoin, twMerge } from '@/utils/tw-merge';

import { FormError } from '../FormError';
import { FormLabel } from '../FormLabel';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  id: string;
  name: string;
  label: string;
  labelClasses?: string;
  subtitle?: string;
  hideLabel?: boolean;
  required?: boolean;
  inputClasses?: string;
  iconButton?: ReactNode;
  horizontal?: boolean;
  /** Whether the input is clearable, only works if the input has a placeholder */
  clearable?: boolean;
  /** Error coming from `react-hook-form` */
  error?: FieldError | string;
  disabled?: boolean;
  readOnly?: boolean;
  onUnmount?: (value: unknown) => void;
  ref?: React.Ref<HTMLInputElement>;
  icon?: IconComponent;
  // isLoading?: boolean;
  type?:
    | 'text'
    | 'number'
    | 'password'
    | 'email'
    | 'tel'
    | 'url'
    | 'search'
    | 'date'
    | 'time'
    | 'datetime-local'
    | 'month'
    | 'week';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      label,
      labelClasses,
      subtitle,
      hideLabel,
      className,
      inputClasses,
      required,
      error,
      disabled,
      clearable,
      readOnly,
      type = 'text',
      icon,
      iconButton,
      onUnmount,
      onChange,
      onFocus,
      onBlur,
      horizontal,
      // isLoading,
      ...props
    },
    ref
  ) => {
    const IconComponent = icon;
    const [firefoxVersion, setFirefoxVersion] = useState<number | undefined>();
    const containerRef = useRef<HTMLDivElement>(null);

    /*
      There's a bug in Firefox 109 where the calendar icon cant be hidden using ::-webkit-calendar-picker-indicator.
      https://bugzilla.mozilla.org/show_bug.cgi?id=1812397

      We temporarily show the browser icon, untill the Firefox bug is fixed.
    */
    useEffect(() => {
      if (type !== 'date') return;

      const userAgent = navigator.userAgent;
      const regex = /Firefox\/(?<majorRelease>\d+(\.\d+)?)/;
      const match = userAgent.match(regex);
      const FirefoxVersion = match ? Number(match?.groups?.majorRelease) : null;

      if (FirefoxVersion) {
        setFirefoxVersion(FirefoxVersion);
      }
    }, [type]);

    /**
     * There are cases where the input is unmounted before the user has finished typing.
     * We need to call the onUnmount callback to be able to clean up the state.
     */
    const prevValueRef = useRef<
      React.ReactHTMLElement<HTMLInputElement>['props']['defaultValue']
    >(props.defaultValue ? props.defaultValue : null);
    const isFocusedRef = useRef(false);

    useUnmountEffect(() => {
      if (onUnmount && isFocusedRef.current) {
        onUnmount(prevValueRef.current);
      }
    });

    const showCalendarIcon =
      type === 'date' && (!firefoxVersion || firefoxVersion < 109);
    const errorId = `${id}-error`;

    return (
      <div
        className={twMerge('style-input-label', className)}
        ref={containerRef}
      >
        <div className={horizontal ? 'flex min-w-5 items-center gap-2' : ''}>
          <FormLabel
            htmlFor={id}
            className={twMerge(
              twJoin(
                'mb-1 block',
                hideLabel ? 'sr-only' : 'mb-1',
                horizontal ? 'min-w-5' : ''
              ),
              labelClasses
            )}
            required={required}
          >
            {label}
          </FormLabel>

          {subtitle && (
            <p className="style-input-label mb-1 text-gray-800">{subtitle}</p>
          )}

          <div className="w-full">
            <div className="relative">
              {IconComponent && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-placeholder">
                  <IconComponent />
                </div>
              )}

              <input
                id={id}
                name={name}
                ref={ref}
                type={type}
                disabled={disabled}
                readOnly={readOnly}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
                onFocus={(e) => {
                  isFocusedRef.current = true;
                  onFocus?.(e);
                }}
                onBlur={(e) => {
                  isFocusedRef.current = false;
                  onBlur?.(e);
                }}
                className={twMerge(
                  twJoin(
                    'peer h-5 w-full rounded-sm border border-gray-500 bg-white px-2 transition-all',
                    'placeholder:text-placeholder',
                    'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    'disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-600',
                    IconComponent && '!pl-5',
                    type === 'date' && '!pr-4',
                    error &&
                      '-mb-px !rounded-b-none !border-red-500 !text-red-800 focus:!ring-red-500'
                  ),
                  inputClasses
                )}
                onChange={(e) => {
                  onChange?.(e);
                  prevValueRef.current = e.target.value;
                }}
                {...props}
              />

              {iconButton && (
                <div className="absolute right-0.5 top-1/2 mr-0.5 -translate-y-1/2">
                  {iconButton}
                </div>
              )}

              {clearable && (
                <Button
                  size="iconSmall"
                  type="button"
                  onClick={() => {
                    const input = containerRef.current?.querySelector('input');

                    if (input) {
                      input.value = '';
                      input.dispatchEvent(
                        new Event('change', { bubbles: true })
                      );
                      input.focus();
                    }
                  }}
                  variant="white"
                  icon={RegularXmark}
                  iconPosition="right"
                  className="absolute right-0.5 top-1/2 mr-0.5 -translate-y-1/2 peer-placeholder-shown:hidden"
                />
              )}

              {showCalendarIcon && (
                <button
                  onClick={() => {
                    const input = containerRef.current?.querySelector('input');
                    input?.showPicker?.();
                  }}
                  type="button"
                  className="absolute inset-y-0 right-0 -mt-px flex items-center px-2"
                >
                  <RegularCalendar />
                </button>
              )}

              {/* <Transitions.FadeIn show={!!isLoading} unmount appear>
              <div className="absolute inset-y-2 right-0 flex items-center pl-2 pr-3">
                <RegularSpinner className="text-gray-700 animate-spin" size={16} />
              </div>
            </Transitions.FadeIn> */}
            </div>
          </div>

          {error && (
            <FormError className="!rounded-t-none" id={errorId}>
              {typeof error === 'string' ? error : error.message}
            </FormError>
          )}
        </div>
      </div>
    );
  }
);
Input.displayName = 'Input';
